import React, { useEffect, useCallback, useMemo } from 'react';
import qs from 'qs';
import { Switch, Route, useLocation, Redirect } from 'react-router-dom';
import { Suspense } from 'react';
import { Spin } from 'antd';
import styled from 'styled-components';
import theme from '@config/theme';
import PrivateRouteWithDeps from '@ui/Routing/PrivateRouteWithDeps';
import ExceptionCodeManager from '@features/QualityAssurance/ExceptionCodes/ExceptionCodeManager';

// Lazy load components
const CreateEditArchetype = React.lazy(() => import('@features/ItemPricing/pages/CreateEditArchetype'));
const CreateEditOption = React.lazy(() => import('@features/ItemPricing/pages/CreateEditOption'));
const CreateEditWoodPart = React.lazy(() => import('@features/ItemPricing/pages/CreateEditWoodPart'));
const CustomerBrowser = React.lazy(() => import('@features/Customer/pages/CustomerBrowser'));
const CustomerDetail = React.lazy(() => import('@features/Customer/pages/CustomerDetail'));
const CustomerPriceList = React.lazy(() => import('@features/Customer/pages/CustomerPriceList'));
const DataParsers = React.lazy(() => import('@features/DevTools/pages/DataParsers'));
const EditPricing = React.lazy(() => import('@features/ItemPricing/pages/EditPricing'));
const InventoryDetail = React.lazy(() => import('@features/Inventory/pages/InventoryDetail'));
const InventoryList = React.lazy(() => import('@features/Inventory/pages/InventoryBrowser'));
const Login = React.lazy(() => import('@features/Auth/pages/Login'));
const NCFileAnalyzer = React.lazy(() => import('@features/DevTools/pages/NCFileAnalyzer'));
const NameGame = React.lazy(() => import('@features/DevTools/pages/NameGame'));
const OrdersList = React.lazy(() => import('@features/Orders/pages/OrderRecords'));
const PartNonComplianceScanner = React.lazy(() => import('@features/QualityAssurance/NonCompliance/pages/NonComplianceScanner'));
const PartRecord = React.lazy(() => import('@features/PartDetail/pages/PartRecord'));
const PurchaseOrderBrowser = React.lazy(() => import('@features/PurchaseOrders/pages/PurchaseOrderBrowser'));
const QATicketBrowser = React.lazy(() => import('@features/QualityAssurance/QATickets/pages/QATicketBrowser'));
const QATicketDetail = React.lazy(() => import('@features/QualityAssurance/QATickets/pages/QATicketDetail'));
const SalesOrder = React.lazy(() => import('@features/ProductionSchedule/pages/SalesOrders'));
const SalesOrderRecord = React.lazy(() => import('@features/Orders/pages/SalesOrderRecord'));
const ShipmentDetail = React.lazy(() => import('@features/Shipment/pages/ShipmentDetail'));
const ShipmentList = React.lazy(() => import('@features/Shipment/pages/ShipmentList'));
const ShopRouter = React.lazy(() => import('@features/ProductionSchedule/pages/ShopRouter'));
const ShopScanner = React.lazy(() => import('@features/ProductionSchedule/pages/ShopScanner'));
const ShopScannerManualEntry = React.lazy(() => import('@features/ProductionSchedule/pages/ShopScannerManualEntry'));
const UniversalDensityCalculatorByPart = React.lazy(() => import('@features/DevTools/DensityCalculator/UniversalDensityCalculatorByPart'));
const VendorBrowser = React.lazy(() => import('@features/Vendor/pages/VendorBrowser'));
const VendorDetail = React.lazy(() => import('@features/Vendor/pages/VendorDetail'));
const WorkOrderRecord = React.lazy(() => import('@features/Orders/pages/WorkOrderRecord'));
const PartTerms = React.lazy(() => import('@features/ProductDevelopment/pages/PartTerms'));
const ProductDevTasks = React.lazy(() => import('@features/ProductDevelopment/pages/ProductDevTasks'));
const TestPDF = React.lazy(() => import('@shared/pdf/TestPDF'));
const PurchaseOrderPDFView = React.lazy(() => import('@features/PurchaseOrders/pages/PurchaseOrderPDFView'));
const PurchaseOrderForm = React.lazy(() => import('@features/PurchaseOrders/pages/PurchaseOrderRecord'));
const ReceiverBrowser = React.lazy(() => import('@features/Receivers/pages/ReceiverBrowser'));
const ReceiverRecord = React.lazy(() => import('@features/Receivers/pages/ReceiverRecord'));
const VacationCalendar = React.lazy(() => import('@features/Personnel/VacationCalendar/pages/VacationCalendar'));
const ReportsBrowser = React.lazy(() => import('@features/Reports/pages/ReportsBrowser'));
const SalesAnalysisPage = React.lazy(() => import('@features/Reports/Sales/SalesAnalysisPage'));
const QBOImportPage = React.lazy(() => import('@features/Admin/QBOImport/pages/QBOImportPage'));
const AccountingPeriodsPage = React.lazy(() => import('@features/Admin/AccountingPeriods/pages/AccountingPeriodsPage'));

// Styled components
const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background-color: ${theme.palette.neutral.white};

  .ant-spin {
    .ant-spin-dot-item {
      background-color: ${theme.palette.primary.hue};
    }
  }
`;

const AppSections = () => {
  const location = useLocation();

  const titles: Record<string, string> = useMemo(() => ({
    schedule: 'Production Plan',
    router: 'Shop Router',
    pricing: 'Pricing',
    'pricing/edit': 'Edit Pricing',
    orders: 'Orders',
    'util/parsers': 'Utilties',
    customers: 'Customers',
    shipments: 'Shipments',
    scanner: 'Shop Scanner',
    inventory: 'Inventory',
    vendors: 'Vendors',
    'purchase-orders': 'Purchase Orders',
    'nc/report': 'Non-Compliance',
    'nc/scanner': 'Non-Compliance',
    'pricing/new-parts': 'New Parts',
    'reports': 'Reports',
    'reports/sales-analysis': 'Sales Analysis',
    'accounting-periods': 'Accounting Periods',
  }), []);

  const getPageTitle = useCallback((pathname: string, search: string): string => {
    const root = pathname.replace('/', '');
    let title = 'Production Plan'; // Default title

    if (!search) {
      title = titles[root] || 'Production Plan';
    } else {
      const queryParams = qs.parse(search.replace('?', ''));
      if (root === 'orders/create') {
        const { customer, salesOrder } = queryParams;
        if (customer && salesOrder) {
          title = `${customer} Order #${salesOrder} - Detail`;
        } else {
          title = 'Order Detail';
        }
      } else if (root === 'inventory') {
        title = 'Inventory List';
      } else if (root === 'inventory/edit') {
        const { partId } = queryParams;
        if (partId) {
          title = `${partId} - Inventory Detail`;
        } else {
          title = 'Inventory Detail';
        }
      } else if (root === 'shipments/edit') {
        title = 'Shipment Detail';
      } else if (root === 'pricing/body' || root === 'pricing/neck') {
        const { partId } = queryParams;
        if (partId) {
          title = `${partId} - Pricing Detail`;
        } else {
          title = 'Customer Part Detail';
        }
      } else if (root === 'pd') {
        title = 'Product Development';
      } else {
        // Fallback for other search pages if needed, or keep default
        title = titles[root] || 'HELM';
      }
    }
    return title;
  }, [titles]);

  useEffect(() => {
    document.title = getPageTitle(location.pathname, location.search);
  }, [location.pathname, location.search, getPageTitle]);

  return (
    <>
      <Suspense fallback={
        <LoadingWrapper>
          <Spin size="large" tip="Loading..." />
        </LoadingWrapper>
      }>
        <Switch location={location}>
          <Route key="login-root" exact path="/login">
            <Login key="login" />
          </Route>

          <Route key="test-pdf" exact path="/test-pdf">
            <TestPDF />
          </Route>

          {/* ------------------- ORDER MANAGEMENT --------------------- */}
          <PrivateRouteWithDeps
            key="orders-list"
            exact
            path="/orders"
            deps={['customers', 'partData']}
            requiredPermissions={['orders.view']}
          >
            <OrdersList />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="add-sales-order"
            exact
            path="/orders/create"
            deps={['settings', 'inventory', 'configTerms', 'routerSteps', 'customers', 'shopOrderData', 'partData']}
            requiredPermissions={['orders.view', 'orders.edit']}
          >
            <SalesOrderRecord />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="work-order-edit"
            exact
            path="/work-order/edit"
            deps={['settings', 'routerSteps', 'customers', 'inventory', 'partData']}
            requiredPermissions={['router.view', 'router.edit', 'router.scan']}
          >
            <WorkOrderRecord />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="router-edit"
            exact
            path="/router/edit"
            deps={['settings', 'routerSteps', 'customers', 'inventory', 'partData']}
            requiredPermissions={['router.view', 'router.edit', 'router.scan']}
          >
            <WorkOrderRecord />
          </PrivateRouteWithDeps>

          {/* ------------------- PRODUCTION SCHEDULE --------------------- */}

          <PrivateRouteWithDeps
            key="orders-at-root"
            exact
            path="/"
            deps={[]}
            requiredPermissions={['schedule.view', 'shipments.view']}
          >
            <Redirect to="/schedule/week" />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="redirect-to-orders-at-root"
            exact
            path="/schedule"
            deps={[]}
            requiredPermissions={['schedule.view', 'shipments.view']}
          >
            <Redirect to="/schedule/week" />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="orders-at-order-path"
            exact
            path="/schedule/week"
            deps={['settings', 'routerSteps', 'customers', 'inventory']}
            requiredPermissions={['schedule.view', 'shipments.view']}
          >
            <SalesOrder />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="customer-orders-at-order-path"
            exact
            path="/schedule/customer/:customerId?"
            deps={['settings', 'routerSteps', 'customers', 'inventory']}
            requiredPermissions={['schedule.view', 'shipments.view']}
          >
            <SalesOrder />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="next-up-orders-at-order-path"
            exact
            path="/schedule/up-next"
            deps={['settings', 'routerSteps', 'customers', 'inventory']}
            requiredPermissions={['schedule.view']}
          >
            <SalesOrder />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="requirements-at-order-path"
            exact
            path="/schedule/requirements"
            deps={['settings', 'routerSteps', 'customers', 'inventory']}
            requiredPermissions={['schedule.view']}
          >
            <SalesOrder />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="router-root"
            exact
            path="/router"
            deps={['settings', 'routerSteps']}
            requiredPermissions={['router.view']}
          >
            <ShopRouter />
          </PrivateRouteWithDeps>

          {/* ------------------- CUSTOMER --------------------- */}

          <PrivateRouteWithDeps
            key="customer-management"
            exact
            path="/customers"
            deps={['settings', 'customers', 'customerContacts']}
            requiredPermissions={['customers.view']}
          >
            <CustomerBrowser />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="customer-detail"
            exact
            path="/customers/detail"
            deps={['settings', 'shippingAddresses']}
            requiredPermissions={['customers.edit']}
          >
            <CustomerDetail />
          </PrivateRouteWithDeps>

          {/* ------------------- SHIPMENT --------------------- */}

          <PrivateRouteWithDeps
            key="shipment-management"
            exact
            path="/shipments"
            deps={['settings', 'customers', 'partData']}
            requiredPermissions={['shipments.view']}
          >
            <ShipmentList />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="shipment-create"
            exact
            path="/shipments/create"
            deps={['settings', 'routerSteps', 'exceptionCodes', 'partData']}
            requiredPermissions={['shipments.edit']}
          >
            <ShipmentDetail />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="shipment-edit"
            exact
            path="/shipments/edit"
            deps={['settings', 'routerSteps', 'exceptionCodes', 'partData']}
            requiredPermissions={['shipments.edit']}
          >
            <ShipmentDetail />
          </PrivateRouteWithDeps>

          {/* ------------------- CUSTOMER PART MANAGEMENT --------------------- */}

          <PrivateRouteWithDeps
            key="pricing-redirect"
            exact
            path="/tools/part-finder"
            deps={['settings', 'configTerms', 'customers', 'partData', 'inventory']}
            requiredPermissions={['parts.view']}
          >
            <Redirect to="/pricing" />
            <CustomerPriceList />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="pricing-root"
            exact
            path="/pricing"
            deps={['settings', 'configTerms', 'customers', 'partData', 'inventory']}
            requiredPermissions={['parts.view']}
          >
            <CustomerPriceList />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="pricing-edit"
            exact
            path="/pricing/edit"
            deps={['settings', 'configTerms', 'customers', 'partData', 'inventory']}
            requiredPermissions={['parts.edit', 'customers.pricing']}
          >
            <EditPricing />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="pricing-archetype-edit"
            exact
            path="/pricing/archetype/edit"
            deps={['settings']}
            requiredPermissions={['parts.edit']}
          >
            <CreateEditArchetype />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="pricing-material-edit"
            exact
            path="/pricing/material/edit"
            deps={['settings', 'inventory']}
            requiredPermissions={['parts.edit']}
          >
            <CreateEditWoodPart />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="pricing-option-edit"
            exact
            path="/pricing/option/edit"
            deps={['settings']}
            requiredPermissions={['parts.edit']}
          >
            <CreateEditOption />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="pricing-neck"
            exact
            path="/pricing/neck"
            deps={['settings', 'configTerms', 'customers', 'partData', 'inventory']}
            requiredPermissions={['parts.view', 'parts.edit']}
          >
            <PartRecord />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="pricing-body"
            exact
            path="/pricing/body"
            deps={['settings', 'configTerms', 'customers', 'partData', 'inventory']}
            requiredPermissions={['parts.view', 'parts.edit']}
          >
            <PartRecord />
          </PrivateRouteWithDeps>

          {/* ------------------- INVENTORY MANAGEMENT --------------------- */}

          <PrivateRouteWithDeps
            key="inventory-list"
            exact
            path="/inventory"
            deps={['settings', 'inventory', 'glCodes', 'productCodes', 'routerSteps', 'partData']}
            requiredPermissions={['inventory.view']}
          >
            <InventoryList />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="inventory-detail"
            exact
            path="/inventory/edit/:partId?"
            deps={['settings', 'inventory', 'glCodes', 'productCodes', 'routerSteps', 'vendors', 'partData']}
            requiredPermissions={['inventory.view', 'inventory.edit']}
          >
            <InventoryDetail quickEdit={false} />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="inventory-create"
            exact
            path="/inventory/new"
            deps={['settings', 'inventory', 'glCodes', 'productCodes', 'routerSteps', 'vendors', 'partData']}
            requiredPermissions={['inventory.edit']}
          >
            <InventoryDetail quickEdit={false} />
          </PrivateRouteWithDeps>

          {/* ------------------- PURCHASE ORDERS --------------------- */}

          <PrivateRouteWithDeps
            key="purchase-order-list"
            exact
            path="/purchase-orders"
            deps={['vendors', 'glCodes', 'settings', 'purchaseOrders']}
            requiredPermissions={['purchasing.view']}
          >
            <PurchaseOrderBrowser />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="purchase-order-create"
            exact
            path="/purchase-orders/create"
            deps={['glCodes', 'settings', 'vendors', 'shipMethods', 'terms']}
            requiredPermissions={['purchasing.edit']}
          >
            <PurchaseOrderForm />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="purchase-order-edit"
            exact
            path="/purchase-orders/edit"
            deps={['glCodes', 'settings', 'vendors', 'shipMethods', 'terms']}
            requiredPermissions={['purchasing.edit']}
          >
            <PurchaseOrderForm />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="purchase-order-print"
            exact
            path="/purchase-orders/print"
            deps={['settings', 'vendors', 'purchaseOrders']}
            requiredPermissions={['purchasing.view']}
          >
            <PurchaseOrderPDFView />
          </PrivateRouteWithDeps>

          {/* ------------------- RECEIVERS --------------------- */}

          <PrivateRouteWithDeps
            key="receivers-list"
            exact
            path="/receivers"
            deps={['settings', 'receivers']}
            requiredPermissions={['purchasing.receivers', 'purchasing.view']}
          >
            <ReceiverBrowser />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="receiver-create"
            exact
            path="/receivers/create"
            deps={['settings', 'receivers', 'vendors', 'glCodes', 'productCodes']}
            requiredPermissions={['purchasing.receivers']}
          >
            <ReceiverRecord />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="receiver-edit"
            exact
            path="/receivers/edit"
            deps={['settings', 'receivers', 'vendors', 'glCodes', 'productCodes']}
            requiredPermissions={['purchasing.receivers']}
          >
            <ReceiverRecord />
          </PrivateRouteWithDeps>

          {/* ------------------- VENDOR MANAGEMENT --------------------- */}

          <PrivateRouteWithDeps
            key="vendor-list"
            exact
            path="/vendors"
            deps={['settings', 'vendors']}
            requiredPermissions={['purchasing.vendors', 'purchasing.view']}
          >
            <VendorBrowser />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="vendor-detail"
            exact
            path="/vendors/detail"
            deps={['settings', 'vendors', 'terms']}
            requiredPermissions={['purchasing.vendors']}
          >
            <VendorDetail />
          </PrivateRouteWithDeps>

          {/* ------------------- UTILITY --------------------- */}

          <PrivateRouteWithDeps
            key="parsers-list"
            exact
            path="/util/parsers"
            deps={['settings', 'configTerms', 'inventory', 'routerSteps', 'customers']}
            requiredPermissions={['admin.utilities']}
          >
            <DataParsers />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="vacation-calendar"
            exact
            path="/util/vacation-calendar"
            deps={[]}
            requiredPermissions={['admin.vacation-calendar']}
          >
            <VacationCalendar />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="nc-file-analyzer"
            exact
            path="/pd/nc-file-analyzer"
            deps={[]}
            requiredPermissions={['admin.utilities', 'parts.edit', 'parts.pd-tools']}
          >
            <NCFileAnalyzer />
          </PrivateRouteWithDeps>

          <Route key="udc-deprecated" exact path="/util/udc">
            <Redirect to="/tools/udc" />
          </Route>

          <PrivateRouteWithDeps
            key="weight-chart-by-part"
            exact
            path="/pd/density-calculator"
            deps={['settings', 'customers']}
            requiredPermissions={['admin.utilities', 'parts.edit', 'parts.pd-tools']}
          >
            <UniversalDensityCalculatorByPart />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="name-game"
            exact
            path="/admin/name-game"
            deps={['settings', 'configTerms']}
            requiredPermissions={['admin.utilities']}
          >
            <NameGame />
          </PrivateRouteWithDeps>

          {/* ------------------- SCANNERS --------------------- */}

          <PrivateRouteWithDeps
            key="scanner-root"
            exact
            path="/scanner"
            deps={['settings', 'routerSteps', 'inventory']}
            requiredPermissions={['router.scan']}
          >
            <ShopScanner />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="scanner-manual"
            exact
            path="/scanner/manual-entry"
            deps={['settings', 'routerSteps']}
            requiredPermissions={['router.scan']}
          >
            <ShopScannerManualEntry />
          </PrivateRouteWithDeps>

          {/* ------------------- QUALITY ASSURANCE & NON_COMPLIANCE --------------------- */}

          <PrivateRouteWithDeps
            key="qa-ticket-browser"
            exact
            path="/qa/tickets"
            deps={['settings']}
            requiredPermissions={['qa.view']}
          >
            <QATicketBrowser />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="qa-ticket-new"
            exact
            path="/qa/ticket/new"
            deps={['settings', 'customers', 'routerSteps', 'operatorData', 'ncConfig']}
            requiredPermissions={['qa.edit']}
          >
            <QATicketDetail />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="qa-ticket-edit"
            exact
            path="/qa/ticket/edit"
            deps={['settings', 'customers', 'routerSteps', 'operatorData', 'ncConfig']}
            requiredPermissions={['qa.edit']}
          >
            <QATicketDetail />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="non-compliance-scanner"
            exact
            path="/qa/scanner"
            deps={['settings']}
            requiredPermissions={['qa.view', 'router.scan']}
          >
            <PartNonComplianceScanner />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="qa-exception-codes"
            exact
            path="/qa/exceptions"
            deps={['settings']}
            requiredPermissions={['qa.exceptions']}
          >
            <ExceptionCodeManager />
          </PrivateRouteWithDeps>

          {/* ------------------- NEW PARTS --------------------- */}

          <PrivateRouteWithDeps
            key="new-parts"
            exact
            path="/pd/queue"
            deps={['settings', 'openOrders', 'configTerms']}
            requiredPermissions={['parts.dev-queue']}
          >
            <ProductDevTasks />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="part-terms"
            exact
            path="/pd/part-terms"
            deps={['settings', 'configTerms']}
            requiredPermissions={['parts.terms']}
          >
            <PartTerms />
          </PrivateRouteWithDeps>

          {/* ------------------- REPORTS --------------------- */}

          <PrivateRouteWithDeps
            key="reports"
            exact
            path="/reports"
            deps={[]}
            requiredPermissions={['admin.reports']}
          >
            <ReportsBrowser />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="reports-sales-analysis"
            exact
            path="/reports/sales-analysis"
            deps={[]}
            requiredPermissions={['admin.reports']}
          >
            <SalesAnalysisPage />
          </PrivateRouteWithDeps>

          {/* ------------------- ADMIN TOOLS --------------------- */}

          <PrivateRouteWithDeps
            key="qbo-import"
            exact
            path="/admin/qbo-import"
            deps={['vendors', 'inventory']}
            requiredPermissions={['admin.qbo-import']}
          >
            <QBOImportPage />
          </PrivateRouteWithDeps>

          <PrivateRouteWithDeps
            key="accounting-periods"
            exact
            path="/admin/accounting-periods"
            deps={['inventory']}
            requiredPermissions={['admin.period-closing']}
          >
            <AccountingPeriodsPage />
          </PrivateRouteWithDeps>

        </Switch>
      </Suspense>
    </>
  );
};

export default AppSections;
